import { CalendarCheck, Clock, CheckCircle, Hourglass } from '@phosphor-icons/react';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useStudentTheme } from '../context/StudentThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getDaysUntil(dateStr) {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

function getEndTime(time, durationMinutes) {
    if (!time || !durationMinutes) return '';
    const [h, m] = time.split(':').map(Number);
    const total = h * 60 + m + Number(durationMinutes);
    const eh = Math.floor((total / 60) % 24);
    const em = total % 60;
    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
}

function getLabel(days) {
    if (days < 0) return 'Afgelopen';
    if (days === 0) return 'Vandaag!';
    if (days === 1) return 'Morgen';
    return `Over ${days} dagen`;
}

export default function NextLessonCard({ lesson, isInstructor, onConfirmed }) {
    const theme = useStudentTheme();
    const [confirming, setConfirming] = useState(false);
    const [status, setStatus] = useState(lesson?.confirmation_status || 'confirmed');

    if (!lesson) return null;

    const days = getDaysUntil(lesson.date);
    const isToday = days === 0;
    const isTomorrow = days === 1;
    const isPast = days < 0;

    if (isPast) return null;

    const borderColor = isToday ? theme.primary : isTomorrow ? '#F59E0B' : '#3B82F6';
    const isPending = status === 'pending';

    const confirm = async () => {
        if (confirming) return;
        setConfirming(true);
        try {
            await axios.post(`${API}/scheduled-lessons/${lesson.id}/confirm`, null, { withCredentials: true });
            setStatus('confirmed');
            toast.success('Les bevestigd ✅');
            onConfirmed?.();
        } catch (e) {
            toast.error('Bevestigen mislukt — probeer opnieuw');
        }
        setConfirming(false);
    };

    return (
        <div
            className="bg-[#1A202C] rounded-xl border-2 p-5 relative overflow-hidden"
            style={{ borderColor }}
            data-testid="next-lesson-card"
        >
            <div className="absolute inset-0 opacity-5"
                style={{ background: `radial-gradient(circle at center, ${borderColor}, transparent 70%)` }} />

            <div className="relative flex items-center gap-5">
                {/* Big day indicator */}
                <div className={`text-center min-w-[80px] ${isToday ? 'animate-pulse-glow' : ''}`}>
                    <div className="text-4xl font-bold leading-none" style={{ color: borderColor, fontFamily: 'Outfit, sans-serif' }}>
                        {isToday ? 'NU' : isTomorrow ? '1' : days}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">
                        {isToday ? 'Vandaag' : isTomorrow ? 'dag' : 'dagen'}
                    </div>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <CalendarCheck size={18} style={{ color: borderColor }} />
                        <span className="text-sm font-semibold text-white">Volgende Rijles</span>
                        {/* Confirmation status badge */}
                        {isPending ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-400/40 rounded-full px-1.5 py-0.5" data-testid="lesson-status-pending">
                                <Hourglass size={9} weight="fill" />
                                Wacht op bevestiging
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-400/40 rounded-full px-1.5 py-0.5" data-testid="lesson-status-confirmed">
                                <CheckCircle size={9} weight="fill" />
                                Bevestigd
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-400 mb-1 flex-wrap">
                        <span>{lesson.date}</span>
                        {lesson.time && (
                            <span className="flex items-center gap-1">
                                <Clock size={14} />
                                {lesson.time}{lesson.duration_minutes ? ` – ${getEndTime(lesson.time, lesson.duration_minutes)}` : ''}
                            </span>
                        )}
                        <span>{lesson.duration_minutes} min</span>
                    </div>
                    <p className="text-sm font-medium" style={{ color: borderColor }}>
                        {getLabel(days)}
                    </p>
                    {lesson.notes && (
                        <p className="text-xs text-gray-500 mt-1">{lesson.notes}</p>
                    )}
                </div>
            </div>

            {/* Confirm button — only for students with a pending lesson */}
            {!isInstructor && isPending && (
                <button
                    onClick={confirm}
                    disabled={confirming}
                    className="relative mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#D4A537] hover:bg-[#E5B84A] disabled:opacity-60 text-[#1A202C] font-semibold py-2.5 transition-colors active:scale-[0.98]"
                    data-testid="confirm-lesson-button"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                    <CheckCircle size={18} weight="fill" />
                    {confirming ? 'Bevestigen…' : 'Bevestig deze les'}
                </button>
            )}
        </div>
    );
}
